import React, { useState } from "react";

import {
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Alert,
    Typography,
    InputAdornment,
    Divider,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";

import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import ElectricalServicesIcon from "@mui/icons-material/ElectricalServices";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { entity_id, Platform } from "../Utils/Const";
import { baseapi } from "../Environmentalurl";


// ======================================================
// APSPDCL BILL FETCH API
// ======================================================

const APSPDCL_BILL_FETCH_URL =
     `${baseapi}/auth/apspdclbillfetch`;

const APSPDCL_REQUEST_CONFIG = {
    ReqType: "PPIAPSPDCLBillFetch",
    Platform: Platform,
    EntityID: entity_id,
    MachineID: "25679a8b314d7fba",
    GeoLocation: "12.9163182,77.6106841",
};

// ======================================================
// TA WALLET ELECTRICITY
// ======================================================

const TAWalletElectricity = () => {

    const [mobile, setMobile] = useState("");

    const [serviceNumber, setServiceNumber] =
        useState("");

    const [captcha, setCaptcha] = useState("");

    const [captchaInput, setCaptchaInput] =
        useState("");

    const [loading, setLoading] =
        useState(false);

    const [billDetails, setBillDetails] =
        useState(null);

    // Used to automatically scroll to the bill after a successful fetch.
    const billDetailsRef = React.useRef(null);

    const [errorMessage, setErrorMessage] =
        useState("");

    // Login popup
    const [loginDialogOpen, setLoginDialogOpen] =
        useState(false);

    const [loginRequestLoading, setLoginRequestLoading] =
        useState(false);


    // ==================================================
    // MOBILE NUMBER
    // ==================================================

    const handleMobileChange = (e) => {
        const value = e.target.value.replace(/\D/g, "");

        if (value.length <= 10) {
            setMobile(value);
            setBillDetails(null);
            setErrorMessage("");
        }
    };


    // ==================================================
    // CAPTCHA
    // ==================================================

    const generateCaptcha = () => {
        const characters = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
        let value = "";

        for (let i = 0; i < 6; i++) {
            value += characters.charAt(
                Math.floor(Math.random() * characters.length)
            );
        }

        setCaptcha(value);
        setCaptchaInput("");
        setBillDetails(null);
        setErrorMessage("");
    };

    React.useEffect(() => {
        generateCaptcha();
    }, []);

    // Automatically scroll to Bill Details after a successful API response.
    React.useEffect(() => {
        if (!billDetails) return;

        const scrollToBill = () => {
            billDetailsRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        };

        // Wait for the bill section to be rendered before scrolling.
        const timer = window.setTimeout(scrollToBill, 100);

        return () => window.clearTimeout(timer);
    }, [billDetails]);

    // ==================================================
    // FETCH BILL
    // ==================================================

    const handleFetchBill = async () => {
        if (
            mobile.length !== 10 ||
            !serviceNumber.trim() ||
            !captchaInput.trim()
        ) {
            return;
        }

        // Validate CAPTCHA before calling the API.
        if (
            captchaInput.trim().toUpperCase() !==
            captcha.toUpperCase()
        ) {
            setBillDetails(null);
            setErrorMessage("Invalid CAPTCHA. Please enter the characters shown.");
            return;
        }

        setLoading(true);
        setBillDetails(null);
        setErrorMessage("");

        const requestBody = {
            ...APSPDCL_REQUEST_CONFIG,
            Mobilenumber: mobile,
            SCNO: serviceNumber.trim(),
        };

        try {
            const response = await fetch(
                APSPDCL_BILL_FETCH_URL,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(requestBody),
                }
            );

            let responseData = null;

            try {
                responseData = await response.json();
            } catch {
                responseData = null;
            }

            if (!response.ok) {
                setErrorMessage(
                    responseData?.Message ||
                    responseData?.message ||
                    "Unable to access the bill."
                );
                return;
            }

            // APSPDCL returns bill data inside BillerInfo.Info.
            // Keep responseData.Info as a fallback for older/direct responses.
            const responseCode = String(
                responseData?.Response_Code ??
                responseData?.BillerInfo?.Response_Code ??
                ""
            ).trim();

            const responseMessage = String(
                responseData?.Message ??
                responseData?.BillerInfo?.Message ??
                ""
            ).trim();

            const info =
                responseData?.BillerInfo &&
                    typeof responseData.BillerInfo === "object"
                    ? responseData.BillerInfo
                    : responseData?.Info &&
                        typeof responseData.Info === "object"
                        ? responseData.Info
                        : {};

            const infoStatus = String(
                info?.STATUS ?? ""
            ).trim().toUpperCase();

            const infoMessage = String(
                info?.MSG ?? ""
            ).trim();

            // Account is not active
            if (
                responseCode === "40003" ||
                responseMessage.toLowerCase() ===
                "account is not active"
            ) {
                setBillDetails(null);
                setErrorMessage("Account is not active");
                return;
            }

            // Unauthorized request
            if (
                responseCode === "31503" ||
                infoMessage.toUpperCase() ===
                "UNAUTHORIZED REQUEST."
            ) {
                setBillDetails(null);
                setErrorMessage("Invalid Request");
                return;
            }

            // Any failure response
            if (
                responseCode !== "3150" ||
                infoStatus === "FAIL"
            ) {
                setBillDetails(null);
                setErrorMessage(
                    infoMessage ||
                    responseMessage ||
                    "Unable to Access the Bill"
                );
                return;
            }

            // Successful bill response
            if (
                responseCode === "3150" &&
                infoStatus === "VALID"
            ) {
                setErrorMessage("");

                // Keep the complete APSPDCL bill-fetch response in
                // sessionStorage so the TA Wallet login page can
                // build the final APSPDCL payment request after TPIN.
                sessionStorage.setItem(
                    "taWalletBillFetchResponse",
                    JSON.stringify(responseData)
                );

                // If the bill-fetch service supplies an OrderID,
                // preserve it for the payment API.
                const billOrderID =
                    responseData?.OrderID ||
                    responseData?.BillerInfo?.OrderID ||
                    info?.OrderID ||
                    "";

                if (billOrderID) {
                    sessionStorage.setItem(
                        "taWalletOrderID",
                        String(billOrderID)
                    );
                }

                setBillDetails({
                    status: info?.STATUS || "-",
                    scno:
                        info?.SCNO ||
                        serviceNumber.trim() ||
                        "-",
                    consumerName:
                        info?.CONSUMERNAME || "-",
                    circleName:
                        info?.CIRCLENAME || "-",
                    arrears:
                        info?.ARREARS ?? "0",
                    currentDemand:
                        info?.CURRENTDEMAND ?? "0",
                    acdamt:
                        info?.ACDAMT ?? "0",
                    rcFees:
                        info?.RCFEES ?? "0",
                    totalAmount:
                        info?.TOTAMOUNT ??
                        info?.TOTALAMOUNT ??
                        "0",
                    readingDate:
                        info?.RDGDATE || "-",
                    dueDate:
                        info?.DUEDATE || "-",
                    disconnectDate:
                        info?.DISCDATE || "-",
                });

                return;
            }

            // Unexpected response
            setBillDetails(null);
            setErrorMessage(
                infoMessage ||
                responseMessage ||
                "Unable to Access the Bill"
            );
        } catch (error) {
            console.error("APSPDCL Bill Fetch Error:", error);

            setErrorMessage(
                "Unable to access the bill. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };


    // ==================================================
    // PAYMENT
    // ==================================================

    const handlePayment = () => {

        setErrorMessage("");

        // Open login popup
        setLoginDialogOpen(true);
    };


    // ==================================================
    // TA WALLET OTP REQUEST
    // ==================================================

    const handleLoginRedirect = async () => {

        const mobileNumber = mobile.trim();

        if (mobileNumber.length !== 10) {
            setErrorMessage(
                "Please enter a valid 10-digit mobile number."
            );
            return;
        }

        if (!serviceNumber.trim()) {
            setErrorMessage(
                "Please enter your Service Number / USC."
            );
            return;
        }

        setErrorMessage("");
        setLoginRequestLoading(true);

        // Store the mobile number and service number so they
        // are available on the TA Wallet OTP page.
        sessionStorage.setItem(
            "taWalletMobile",
            mobileNumber
        );

        sessionStorage.setItem(
            "taWalletServiceNumber",
            serviceNumber.trim()
        );

        const requestBody = {
            ReqType: "One_userlogin",
            MobileNumber: mobileNumber,
            EntityID: entity_id,
            SSOID: "-",
            MemberID: "26384597149",
            Statement: "LoginWithOTP",
            MachineID: "b3c8d684-0b76-4858-afd3-d4a6d977724f",
            Platform: 1,
            Geolocation: "17.111,17.99",
        };

        try {
            const response = await fetch(
                 `${baseapi}/auth/walletexistance`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(requestBody),
                }
            );

            let responseData = null;

            try {
                responseData = await response.json();
            } catch {
                responseData = null;
            }

            const responseCode = String(
                responseData?.Response_Code ?? ""
            ).trim();

            if (
                !response.ok ||
                responseCode !== "1110"
            ) {
                setErrorMessage(
                    responseData?.Message ||
                    responseData?.message ||
                    "Unable to send OTP. Please try again."
                );
                return;
            }

            // MessageRefID is required by the OTP validation API.
            if (responseData?.MessageRefID) {
                sessionStorage.setItem(
                    "taWalletMessageRefID",
                    String(responseData.MessageRefID)
                );
            }

            // Keep the wallet-existence response for the next step.
            sessionStorage.setItem(
                "taWalletExistenceResponse",
                JSON.stringify(responseData)
            );

            setLoginDialogOpen(false);

            window.location.href =
                "/ta-wallet-login";

        } catch (error) {
            console.error(
                "TA Wallet OTP Request Error:",
                error
            );

            setErrorMessage(
                "Unable to send OTP. Please try again."
            );
        } finally {
            setLoginRequestLoading(false);
        }
    };
    // ==================================================
    // MAIN RETURN
    // ==================================================

    return (

        <Box
            sx={{
                minHeight: "100vh",

                width: "100%",

                backgroundColor: "#ffffff",

                display: "flex",

                justifyContent: "center",

                alignItems: "center",

                px: {
                    xs: 1.5,
                    sm: 3,
                    md: 4,
                },

                py: {
                    xs: 2,
                    sm: 3,
                    md: 4,
                },

                boxSizing: "border-box",
            }}
        >

            {/* ==================================================
                MAIN CARD
            ================================================== */}

            <Card
                elevation={0}
                sx={{
                    width: "100%",

                    maxWidth: 600,

                    borderRadius: {
                        xs: "12px",
                        sm: "16px",
                    },

                    border:
                        "1px solid #dfe7e3",

                    backgroundColor:
                        "#ffffff",

                    boxShadow:
                        "0 10px 35px rgba(22,55,39,0.08)",

                    overflow: "hidden",

                    position: "relative",

                    "&::before": {
                        content: '""',

                        position: "absolute",

                        top: 0,

                        left: 0,

                        right: 0,

                        height: "3px",

                        backgroundColor:
                            "#16834d",
                    },
                }}
            >

                {/* ==================================================
                    CARD CONTENT
                ================================================== */}

                <CardContent
                    sx={{
                        p: {
                            xs: 2,
                            sm: 3,
                            md: 4,
                        },

                        "&:last-child": {
                            pb: {
                                xs: 2,
                                sm: 3,
                                md: 4,
                            },
                        },
                    }}
                >


                    {/* ==================================================
                        HEADER
                        APSPDCL | TITLE | TA WALLET
                    ================================================== */}

                    <Box
                        sx={{
                            display: "grid",

                            gridTemplateColumns: {
                                xs: "1fr 1.7fr 1fr",
                                sm: "1fr 2fr 1fr",
                            },

                            alignItems: "center",

                            width: "100%",

                            mb: {
                                xs: 3,
                                sm: 4,
                            },

                            gap: {
                                xs: 1,
                                sm: 2,
                            },
                        }}
                    >

                        {/* APSPDCL LOGO */}

                        <Box
                            sx={{
                                display: "flex",

                                justifyContent:
                                    "flex-start",

                                alignItems: "center",
                            }}
                        >

                            <Box
                                component="img"

                                src="/assets/apspdcl.jpg"

                                alt="APSPDCL Logo"

                                sx={{
                                    height: {
                                        xs: 42,
                                        sm: 52,
                                        md: 60,
                                    },

                                    maxWidth: {
                                        xs: 110,
                                        sm: 150,
                                        md: 180,
                                    },

                                    width: "auto",

                                    objectFit:
                                        "contain",
                                }}
                            />

                        </Box>


                        {/* TITLE */}

                        <Box
                            sx={{
                                textAlign: "center",

                                display: "flex",

                                flexDirection:
                                    "column",

                                alignItems:
                                    "center",

                                justifyContent:
                                    "center",
                            }}
                        >

                            <Typography
                                sx={{
                                    fontSize: {
                                        xs: 20,
                                        sm: 25,
                                        md: 29,
                                    },

                                    fontWeight: 700,

                                    color: "#17221d",

                                    lineHeight: 1.2,

                                    whiteSpace:
                                        "nowrap",
                                }}
                            >
                                Pay Electricity Bill
                            </Typography>


                            <Typography
                                sx={{
                                    mt: 0.8,

                                    fontSize: {
                                        xs: 10.5,
                                        sm: 12,
                                        md: 13.5,
                                    },

                                    color: "#6d7772",

                                    lineHeight: 1.4,

                                    textAlign: "center",
                                }}
                            >
                                Verify your TA Wallet account and pay your electricity bill securely.
                            </Typography>

                        </Box>


                        {/* TA WALLET LOGO */}

                        <Box
                            sx={{
                                display: "flex",

                                justifyContent:
                                    "flex-end",

                                alignItems: "center",
                            }}
                        >

                            <Box
                                component="img"

                                src="/assets/ta_walletlogo.png"

                                alt="TA Wallet Logo"

                                sx={{
                                    height: {
                                        xs: 34,
                                        sm: 42,
                                        md: 48,
                                    },

                                    maxWidth: {
                                        xs: 105,
                                        sm: 140,
                                        md: 165,
                                    },

                                    width: "auto",

                                    objectFit:
                                        "contain",
                                }}
                            />

                        </Box>

                    </Box>


                    {/* ==================================================
                        FETCH/ERROR MESSAGE
                        Show API validation errors above the Mobile Number
                        so the user sees the message immediately without
                        needing to scroll down.
                    ================================================== */}

                    {errorMessage && !loginDialogOpen && (
                        <Alert
                            severity="error"
                            sx={{
                                mb: 2.5,
                                borderRadius: "9px",
                                border: "1px solid #f0caca",
                                backgroundColor: "#fff7f7",
                                color: "#a32626",
                                alignItems: "center",
                                "& .MuiAlert-icon": {
                                    color: "#c43d3d",
                                    fontSize: 22,
                                },
                                "& .MuiAlert-message": {
                                    fontSize: 13,
                                    fontWeight: 500,
                                },
                            }}
                        >
                            {errorMessage}
                        </Alert>
                    )}

                    {/* ==================================================
                        MOBILE NUMBER
                    ================================================== */}

                    <Box
                        sx={{
                            mb: 3,
                        }}
                    >

                        {/* ICON + TITLE */}

                        <Box
                            sx={{
                                display: "flex",

                                alignItems: "center",

                                gap: 1.2,

                                mb: 1.5,
                            }}
                        >

                            <Box
                                sx={{
                                    width: 38,

                                    height: 38,

                                    borderRadius: "9px",

                                    backgroundColor:
                                        "#edf7f1",

                                    display: "flex",

                                    alignItems:
                                        "center",

                                    justifyContent:
                                        "center",

                                    flexShrink: 0,
                                }}
                            >

                                <PhoneAndroidIcon
                                    sx={{
                                        color:
                                            "#16834d",

                                        fontSize: 21,
                                    }}
                                />

                            </Box>


                            <Typography
                                sx={{
                                    fontSize: {
                                        xs: 16,
                                        sm: 18,
                                    },

                                    fontWeight: 600,

                                    color: "#202a25",
                                }}
                            >
                                Mobile Number
                            </Typography>

                        </Box>


                        {/* DESCRIPTION */}

                        <Typography
                            sx={{
                                fontSize: 12.5,

                                color: "#7a837f",

                                mb: 2.5,

                                ml: {
                                    xs: 0,
                                    sm: 5.2,
                                },
                            }}
                        >
                            Enter your mobile number, Service Number / USC and captcha to fetch your latest electricity bill.
                        </Typography>


                        {/* MOBILE NUMBER */}

                        <TextField
                            fullWidth
                            size="small"
                            label="Mobile Number"
                            placeholder="Enter 10-digit mobile number"
                            value={mobile}
                            onChange={handleMobileChange}
                            inputProps={{
                                maxLength: 10,
                                inputMode: "numeric",
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Typography
                                            sx={{
                                                fontSize: 14,
                                                color: "#66716c",
                                            }}
                                        >
                                            +91
                                        </Typography>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                },
                                "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                                {
                                    borderColor: "#8cae9d",
                                },
                                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                    borderColor: "#16834d",
                                },
                                "& .MuiInputLabel-root.Mui-focused": {
                                    color: "#16834d",
                                },
                            }}
                        />

                    </Box>


                    {/* ==================================================
                        SERVICE NUMBER
                    ================================================== */}

                    <>
                        {/* ==================================================
                                    SERVICE NUMBER
                                ================================================== */}

                        <Box sx={{ mb: 3 }}>

                            {/* SERVICE TITLE */}

                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.2,
                                    mb: 0.8,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 38,
                                        height: 38,
                                        borderRadius: "9px",
                                        backgroundColor: "#edf7f1",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <ElectricalServicesIcon
                                        sx={{
                                            color: "#16834d",
                                            fontSize: 21,
                                        }}
                                    />
                                </Box>

                                <Typography
                                    sx={{
                                        fontSize: {
                                            xs: 16,
                                            sm: 17,
                                        },
                                        fontWeight: 600,
                                        color: "#202a25",
                                    }}
                                >
                                    Electricity Service Details
                                </Typography>
                            </Box>

                            {/* DESCRIPTION */}

                            <Typography
                                sx={{
                                    fontSize: 12.5,
                                    color: "#7a837f",
                                    mb: 2,
                                    ml: {
                                        xs: 0,
                                        sm: 5.2,
                                    },
                                }}
                            >
                                Enter your Service Number / USC and captcha to fetch your latest electricity bill.
                            </Typography>

                            {/* SERVICE NUMBER */}

                            <Box sx={{ mb: 2.5 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Service Number / USC"
                                    placeholder="Enter service number / USC"
                                    value={serviceNumber}
                                    onChange={(e) => {
                                        setServiceNumber(
                                            e.target.value.toUpperCase()
                                        );
                                        setBillDetails(null);
                                        setErrorMessage("");
                                    }}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "9px",
                                            backgroundColor: "#ffffff",
                                        },
                                        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                                        {
                                            borderColor: "#8cae9d",
                                        },
                                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                                        {
                                            borderColor: "#16834d",
                                            borderWidth: "1.5px",
                                        },
                                        "& .MuiInputLabel-root.Mui-focused":
                                        {
                                            color: "#16834d",
                                        },
                                    }}
                                />
                            </Box>

                            {/* CAPTCHA / SECURITY VERIFICATION */}

                            <Box
                                sx={{
                                    p: {
                                        xs: 1.5,
                                        sm: 2,
                                    },
                                    border: "1px solid #dfe8e3",
                                    borderRadius: "10px",
                                    backgroundColor: "#f8fbf9",
                                    mb: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                        mb: 1.3,
                                    }}
                                >
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: 12.5,
                                                fontWeight: 700,
                                                color: "#29362f",
                                            }}
                                        >
                                            Security Verification
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontSize: 11,
                                                color: "#7a837f",
                                                mt: 0.25,
                                            }}
                                        >
                                            Enter the characters shown below
                                        </Typography>
                                    </Box>

                                    <Typography
                                        sx={{
                                            fontSize: 10.5,
                                            fontWeight: 600,
                                            color: "#16834d",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        CAPTCHA
                                    </Typography>
                                </Box>

                                <Box
                                    sx={{
                                        display: "flex",
                                        gap: 1.2,
                                        alignItems: "center",
                                        flexDirection: {
                                            xs: "column",
                                            sm: "row",
                                        },
                                    }}
                                >
                                    {/* CAPTCHA INPUT */}
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Enter CAPTCHA"
                                        placeholder="Enter 6 characters"
                                        value={captchaInput}
                                        onChange={(e) =>
                                            setCaptchaInput(
                                                e.target.value
                                                    .toUpperCase()
                                                    .replace(
                                                        /[^A-Z0-9]/g,
                                                        ""
                                                    )
                                            )
                                        }
                                        inputProps={{
                                            maxLength: 6,
                                        }}
                                        sx={{
                                            flex: 1,
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "8px",
                                                backgroundColor: "#ffffff",
                                            },
                                            "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                                            {
                                                borderColor: "#8cae9d",
                                            },
                                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                                            {
                                                borderColor: "#16834d",
                                            },
                                            "& .MuiInputLabel-root.Mui-focused":
                                            {
                                                color: "#16834d",
                                            },
                                        }}
                                    />

                                    {/* CAPTCHA IMAGE */}
                                    <Box
                                        sx={{
                                            width: {
                                                xs: "100%",
                                                sm: 190,
                                            },
                                            minWidth: {
                                                xs: "100%",
                                                sm: 190,
                                            },
                                            height: 42,
                                            border: "1px solid #cbd9d1",
                                            borderRadius: "8px",
                                            backgroundColor: "#eef5f1",
                                            position: "relative",
                                            overflow: "hidden",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            backgroundImage:
                                                "repeating-linear-gradient(25deg, transparent 0px, transparent 8px, rgba(22,131,77,0.10) 9px, transparent 10px), repeating-linear-gradient(-20deg, transparent 0px, transparent 11px, rgba(24,70,48,0.08) 12px, transparent 13px)",
                                        }}
                                    >
                                        {/* CAPTCHA NOISE LINES */}
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                inset: 0,
                                                pointerEvents: "none",
                                                background:
                                                    "linear-gradient(8deg, transparent 44%, rgba(22,131,77,0.28) 46%, transparent 48%), linear-gradient(-10deg, transparent 53%, rgba(36,71,54,0.22) 55%, transparent 57%)",
                                            }}
                                        />

                                        {/* CAPTCHA CHARACTERS */}
                                        <Box
                                            sx={{
                                                position: "relative",
                                                zIndex: 1,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                gap: {
                                                    xs: 0.5,
                                                    sm: 0.8,
                                                },
                                                px: 2.5,
                                            }}
                                        >
                                            {captcha.split("").map(
                                                (character, index) => (
                                                    <Typography
                                                        key={`${character}-${index}`}
                                                        component="span"
                                                        sx={{
                                                            display: "inline-block",
                                                            fontFamily:
                                                                "monospace",
                                                            fontSize: {
                                                                xs: 17,
                                                                sm: 19,
                                                            },
                                                            fontWeight: 900,
                                                            color:
                                                                index % 2 === 0
                                                                    ? "#17663d"
                                                                    : "#294b3b",
                                                            transform: `rotate(${index % 2 === 0 ? -7 : 6}deg) skewX(${index % 3 === 0 ? -5 : 3}deg)`,
                                                            textShadow:
                                                                "1px 1px 0 rgba(255,255,255,0.75)",
                                                            userSelect: "none",
                                                        }}
                                                    >
                                                        {character}
                                                    </Typography>
                                                )
                                            )}
                                        </Box>

                                        {/* REFRESH */}
                                        <Box
                                            component="button"
                                            type="button"
                                            onClick={generateCaptcha}
                                            aria-label="Refresh captcha"
                                            title="Refresh CAPTCHA"
                                            sx={{
                                                position: "absolute",
                                                right: 5,
                                                top: 4,
                                                zIndex: 2,
                                                width: 26,
                                                height: 26,
                                                border: "1px solid #c8d8cf",
                                                borderRadius: "50%",
                                                backgroundColor: "rgba(255,255,255,0.9)",
                                                color: "#16834d",
                                                cursor: "pointer",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: 16,
                                                p: 0,
                                                lineHeight: 1,
                                                transition: "all 0.2s ease",
                                                "&:hover": {
                                                    backgroundColor: "#ffffff",
                                                    borderColor: "#16834d",
                                                    transform: "rotate(20deg)",
                                                },
                                            }}
                                        >
                                            ↻
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>

                            {/* FETCH BILL - BELOW INPUTS */}

                            <Button
                                fullWidth
                                variant="contained"
                                disabled={
                                    mobile.length !== 10 ||
                                    !serviceNumber.trim() ||
                                    !captchaInput.trim() ||
                                    loading
                                }
                                onClick={handleFetchBill}
                                endIcon={<ArrowForwardIcon />}
                                sx={{
                                    mt: 2,
                                    height: 44,
                                    borderRadius: "8px",
                                    background: "linear-gradient(135deg, #16834d 0%, #1dbb61 100%)",
                                    textTransform: "none",
                                    fontWeight: 600,
                                    fontSize: 14,
                                    boxShadow: "none",
                                    "&:hover": {
                                        background: "linear-gradient(135deg, #116b3f 0%, #16a653 100%)",
                                        boxShadow: "0 5px 14px rgba(22,131,77,0.18)",
                                    },
                                    "&:disabled": {
                                        backgroundColor: "#dce6e0",
                                        color: "#89948e",
                                    },
                                }}
                            >
                                {loading ? "Fetching Bill..." : "Fetch Bill"}
                            </Button>
                        </Box>


                        {/* ==================================================
                                    BILL DETAILS
                                ================================================== */}

                        {billDetails && (

                            <Box
                                ref={billDetailsRef}
                                sx={{
                                    scrollMarginTop: {
                                        xs: 16,
                                        sm: 24,
                                    },
                                }}
                            >

                                <Divider
                                    sx={{
                                        mb: 3,

                                        borderColor:
                                            "#edf0ee",
                                    }}
                                />


                                {/* BILL TITLE */}

                                <Box
                                    sx={{
                                        display:
                                            "flex",

                                        alignItems:
                                            "center",

                                        gap: 1.2,

                                        mb: 2,
                                    }}
                                >

                                    <Box
                                        sx={{
                                            width: 38,

                                            height: 38,

                                            borderRadius:
                                                "9px",

                                            backgroundColor:
                                                "#edf7f1",

                                            display:
                                                "flex",

                                            alignItems:
                                                "center",

                                            justifyContent:
                                                "center",
                                        }}
                                    >

                                        <ReceiptLongIcon
                                            sx={{
                                                color:
                                                    "#16834d",

                                                fontSize:
                                                    21,
                                            }}
                                        />

                                    </Box>


                                    <Typography
                                        sx={{
                                            fontSize:
                                                17,

                                            fontWeight:
                                                600,

                                            color:
                                                "#202a25",
                                        }}
                                    >
                                        Bill Details
                                    </Typography>

                                </Box>


                                {/* BILL CARD */}

                                <Paper
                                    elevation={0}
                                    sx={{
                                        border: "1px solid #e0e8e3",
                                        borderRadius: "10px",
                                        overflow: "hidden",
                                        backgroundColor: "#ffffff",
                                    }}
                                >
                                    {/* SUCCESS HEADER */}

                                    <Box
                                        sx={{
                                            p: 2,
                                            backgroundColor: "#f8faf9",
                                            borderBottom: "1px solid #e8eeea",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            gap: 2,
                                        }}
                                    >
                                        <Box>
                                            <Typography
                                                sx={{
                                                    fontSize: 11,
                                                    color: "#7c8781",
                                                    mb: 0.4,
                                                }}
                                            >
                                                Bill Status
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    fontSize: 15,
                                                    fontWeight: 700,
                                                    color: "#16834d",
                                                }}
                                            >
                                                {billDetails.status}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ textAlign: "right" }}>
                                            <Typography
                                                sx={{
                                                    fontSize: 11,
                                                    color: "#7c8781",
                                                    mb: 0.4,
                                                }}
                                            >
                                                Total Amount
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    fontSize: 20,
                                                    fontWeight: 800,
                                                    color: "#16834d",
                                                }}
                                            >
                                                ₹{billDetails.totalAmount}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* BILL DETAILS GRID */}

                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: {
                                                xs: "1fr 1fr",
                                                sm: "repeat(3, 1fr)",
                                            },
                                        }}
                                    >
                                        <BillItem
                                            label="Status"
                                            value={billDetails.status}
                                            status
                                        />

                                        <BillItem
                                            label="SCNO"
                                            value={billDetails.scno}
                                        />

                                        <BillItem
                                            label="Consumer Name"
                                            value={billDetails.consumerName}
                                        />

                                        <BillItem
                                            label="Circle Name"
                                            value={billDetails.circleName}
                                        />

                                        <BillItem
                                            label="Arrears"
                                            value={`₹${billDetails.arrears}`}
                                        />

                                        <BillItem
                                            label="Current Demand"
                                            value={`₹${billDetails.currentDemand}`}
                                        />

                                        <BillItem
                                            label="ACD Amount"
                                            value={`₹${billDetails.acdamt}`}
                                        />

                                        <BillItem
                                            label="RC Fees"
                                            value={`₹${billDetails.rcFees}`}
                                        />

                                        <BillItem
                                            label="Total Amount"
                                            value={`₹${billDetails.totalAmount}`}
                                            amount
                                        />

                                        <BillItem
                                            label="Reading Date"
                                            value={billDetails.readingDate}
                                        />

                                        <BillItem
                                            label="Due Date"
                                            value={billDetails.dueDate}
                                        />

                                        <BillItem
                                            label="Disconnect Date"
                                            value={billDetails.disconnectDate}
                                        />
                                    </Box>
                                </Paper>


                                {/* ==================================================
                                            PAYMENT
                                        ================================================== */}

                                <Box
                                    sx={{
                                        mt: 2,

                                        p: 2,

                                        border:
                                            "1px solid #e2e9e5",

                                        borderRadius:
                                            "9px",

                                        backgroundColor:
                                            "#f8faf9",

                                        display:
                                            "flex",

                                        alignItems:
                                        {
                                            xs:
                                                "stretch",

                                            sm:
                                                "center",
                                        },

                                        justifyContent:
                                            "space-between",

                                        gap: 2,

                                        flexDirection:
                                        {
                                            xs:
                                                "column",

                                            sm:
                                                "row",
                                        },
                                    }}
                                >

                                    <Box>

                                        <Typography
                                            sx={{
                                                fontSize:
                                                    13,

                                                fontWeight:
                                                    600,

                                                color:
                                                    "#344139",
                                            }}
                                        >
                                            Ready to make your payment?
                                        </Typography>


                                        <Typography
                                            sx={{
                                                fontSize:
                                                    11.5,

                                                color:
                                                    "#7b8680",

                                                mt:
                                                    0.4,
                                            }}
                                        >
                                            Continue to TA Wallet to
                                            authorize your payment.
                                        </Typography>

                                    </Box>


                                    <Button
                                        variant="contained"

                                        endIcon={
                                            <ArrowForwardIcon />
                                        }

                                        onClick={
                                            handlePayment
                                        }

                                        sx={{
                                            minWidth:
                                            {
                                                xs:
                                                    "100%",

                                                sm:
                                                    165,
                                            },

                                            height:
                                                40,

                                            borderRadius:
                                                "8px",

                                            backgroundColor:
                                                "#16834d",

                                            textTransform:
                                                "none",

                                            fontWeight:
                                                600,

                                            boxShadow:
                                                "none",

                                            "&:hover":
                                            {
                                                backgroundColor:
                                                    "#116b3f",

                                                boxShadow:
                                                    "none",
                                            },
                                        }}
                                    >
                                        Continue to Payment
                                    </Button>

                                </Box>

                            </Box>

                        )}
                    </>

                </CardContent>


                {/* ==================================================
                    FOOTER
                ================================================== */}

                <Divider />

                <Box
                    sx={{
                        py: 1.8,

                        px: 2,

                        textAlign: "center",

                        backgroundColor:
                            "#fcfdfc",
                    }}
                >

                    <Typography
                        sx={{
                            fontSize: 11.5,

                            color: "#89938e",
                        }}
                    >
                        Your payment will be securely processed
                        through TA Wallet.
                    </Typography>

                </Box>

            </Card>


            {/* ==================================================
                PLEASE LOGIN POPUP
            ================================================== */}

            <Dialog
                open={loginDialogOpen}

                onClose={() =>
                    setLoginDialogOpen(false)
                }

                PaperProps={{
                    sx: {
                        width: "100%",

                        maxWidth: 380,

                        borderRadius: "14px",

                        border:
                            "1px solid #dfe7e3",

                        boxShadow:
                            "0 15px 45px rgba(22,55,39,0.15)",

                        overflow: "hidden",
                    },
                }}
            >

                {/* GREEN TOP BORDER */}

                <Box
                    sx={{
                        height: 3,

                        backgroundColor:
                            "#16834d",
                    }}
                />


                <DialogTitle
                    sx={{
                        textAlign: "center",

                        fontSize: 20,

                        fontWeight: 700,

                        color: "#1c2822",

                        pt: 3,

                        pb: 1,
                    }}
                >
                    Please Login
                </DialogTitle>


                <DialogContent
                    sx={{
                        textAlign: "center",

                        px: 4,

                        pb: 1,
                    }}
                >

                    {/* LOGIN ICON */}

                    <Box
                        sx={{
                            width: 52,

                            height: 52,

                            borderRadius: "50%",

                            backgroundColor:
                                "#edf7f1",

                            display: "flex",

                            alignItems:
                                "center",

                            justifyContent:
                                "center",

                            mx: "auto",

                            mb: 1.5,
                        }}
                    >

                        <LockOutlinedIcon
                            sx={{
                                color:
                                    "#16834d",

                                fontSize: 27,
                            }}
                        />

                    </Box>


                    <Typography
                        sx={{
                            fontSize: 13,

                            color: "#737e79",

                            lineHeight: 1.6,
                        }}
                    >
                        Please login to your TA Wallet account
                        to continue with the payment.
                    </Typography>

                    {errorMessage && (
                        <Alert
                            severity="error"
                            sx={{
                                mt: 2,
                                borderRadius: "8px",
                                fontSize: 12,
                                textAlign: "left",
                            }}
                        >
                            {errorMessage}
                        </Alert>
                    )}

                </DialogContent>


                <DialogActions
                    sx={{
                        justifyContent:
                            "center",

                        px: 4,

                        pb: 3,

                        pt: 2,
                    }}
                >

                    <Button
                        variant="contained"

                        onClick={
                            handleLoginRedirect
                        }

                        disabled={loginRequestLoading}

                        sx={{
                            minWidth: 110,

                            height: 40,

                            borderRadius: "8px",

                            backgroundColor:
                                "#16834d",

                            textTransform:
                                "none",

                            fontWeight: 600,

                            boxShadow:
                                "none",

                            "&:hover": {
                                backgroundColor:
                                    "#116b3f",

                                boxShadow:
                                    "none",
                            },
                        }}
                    >
                        {loginRequestLoading
                            ? "Sending OTP..."
                            : "OK"}
                    </Button>

                </DialogActions>

            </Dialog>

        </Box>
    );
};


// ======================================================
// BILL ITEM
// ======================================================

const BillItem = ({
    label,
    value,
    amount = false,
    status = false,
}) => {

    return (

        <Box
            sx={{
                p: 1.8,

                borderRight:
                    "1px solid #edf1ef",

                borderBottom:
                    "1px solid #edf1ef",

                "&:last-child": {
                    borderRight: "none",
                },
            }}
        >

            <Typography
                sx={{
                    fontSize: 10.5,

                    color: "#7c8781",

                    mb: 0.5,
                }}
            >
                {label}
            </Typography>


            <Typography
                sx={{
                    fontSize:
                        amount ? 17 : 12,

                    fontWeight: 700,

                    color:
                        status
                            ? "#16834d"
                            : amount
                                ? "#16834d"
                                : "#29362f",

                    wordBreak:
                        "break-word",
                }}
            >
                {value}
            </Typography>

        </Box>
    );
};


export default TAWalletElectricity;